import { Text, Button, Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, FormControl, FormLabel, Input, ModalFooter, HStack, Spinner, RadioGroup, Radio, Stack, Select } from '@chakra-ui/react'
import React from 'react'
import { ErrorMessage } from '../components/ErrorMessage';
import { useOperation } from '../components/useOperation';
import { IUserInfo } from '@/shared/IUserInfo';
import { EnableSendInvite } from '../components/EnableSendInvite';
import { UserInfoService } from '../services/UserInfoService';

export const AddUserDialog = (props: {
  onClose: () => void;

  onSave: (user: IUserInfo, invite: boolean) => Promise<void>;

  title: string;
  description: string;

  saveLabel: string;
  savingLabel: string;
}) => {

  const defaultUser = { name: '', email: '', id: '' };
  const [user, setUser] = React.useState<IUserInfo>(defaultUser);

  const [invite, setInvite] = React.useState(true);
  const [mode, setMode] = React.useState<'existing' | 'new'>('new');
  const [selectedUserId, setSelectedUserId] = React.useState<string>('');
  const [siteUsers, setSiteUsers] = React.useState<IUserInfo[]>([]);
  const [loadingUsers, setLoadingUsers] = React.useState(false);

  const [onSave, errorMessage, isProcessing] = useOperation(async () => {

    const userToSave = (mode === 'existing')
      ? siteUsers.find(u => u.id === selectedUserId) || defaultUser
      : user;

    await props.onSave(userToSave, invite);
    props.onClose();
  }, [user, invite, mode, selectedUserId, siteUsers]);

  // Load site users when dialog opens
  React.useEffect(() => {
    if (mode === 'existing') {
      setLoadingUsers(true);
      UserInfoService.getSiteUsers().then(users => {
        setSiteUsers(users);
        setLoadingUsers(false);
      }).catch(error => {
        console.error('Failed to load site users:', error);
        setLoadingUsers(false);
      });
    }
  }, [mode]);

  // const navigate = useNavigate();

  const initialRef = React.useRef(null);

  // Update invitation checkbox when mode changes
  React.useEffect(() => {
    setInvite(mode === 'new'); // Existing users don't need portal invitations
  }, [mode]);

  const isDisabled = isProcessing || (mode === 'existing' ? !selectedUserId : !user?.name || !user?.email);

  return (
    <Modal
      initialFocusRef={initialRef}
      finalFocusRef={initialRef}
      size={'xl'}
      isOpen
      onClose={props.onClose}
      preserveScrollBarGap
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{props.title}</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <ErrorMessage error={errorMessage} />
          <Text>{props.description}</Text>

          <FormControl key='UserMode' mt='4'>
            <FormLabel size='md'>User Selection</FormLabel>
            <RadioGroup value={mode} onChange={(value: 'existing' | 'new') => setMode(value)}>
              <Stack direction='row'>
                <Radio value='new'>Add New User</Radio>
                <Radio value='existing'>Select Existing User</Radio>
              </Stack>
            </RadioGroup>
          </FormControl>

          {mode === 'existing' ? (
            <FormControl key='ExistingUser' isRequired mt='4'>
              <FormLabel>Select User</FormLabel>
              {loadingUsers ? (
                <HStack>
                  <Spinner size='sm' />
                  <Text>Loading users...</Text>
                </HStack>
              ) : (
                <Select
                  placeholder='Select a user...'
                  value={selectedUserId}
                  onChange={e => setSelectedUserId(e.target.value)}
                  isDisabled={isProcessing}
                >
                  {siteUsers.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.name} ({user.email})
                    </option>
                  ))}
                </Select>
              )}
              <Text mt='1' fontSize='small' color='fg.subtle'>Select from existing users in the system</Text>
            </FormControl>
          ) : (
            <>
              <FormControl key='UserName' isRequired mt='4'>
                <FormLabel size='md'>User Name</FormLabel>
                <Input ref={initialRef}
                  placeholder='Leo Vector'
                  isDisabled={isProcessing}
                  value={user?.name}
                  onChange={e => setUser({ ...user, name: e.target.value })}
                />
              </FormControl>

              <FormControl key='UserEmail' isRequired mt='4'>
                <FormLabel>User email address</FormLabel>
                <Input
                  type='email'
                  isDisabled={isProcessing}
                  value={user?.email}
                  placeholder='leo.vector@urbandevlab.eu'
                  onChange={e => setUser({ ...user, email: e.target.value })}
                />
              </FormControl>
            </>
          )}
          <EnableSendInvite
            isDisabled={isProcessing || mode === 'existing'}
            sendInviteChecked={invite}
            setSendInviteChecked={setInvite}
            description={mode === 'existing'
              ? 'Existing users are already in the system and do not need portal invitations.'
              : 'Send an invitation to the user to join the EIT UM Label portal.'
            }
          />

        </ModalBody>

        <ModalFooter>
          <Button variant='solid' isDisabled={isDisabled || isProcessing} mr={4} onClick={onSave}>
            <HStack spacing={2}>{isProcessing && <Spinner />}<Text>{isProcessing ? props.savingLabel : props.saveLabel}</Text></HStack>
          </Button>
          <Button variant='outline' onClick={props.onClose}>Cancel</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}